/**
 * Shared field-validation rules.
 *
 * One module, one implementation per rule, used by every form in both
 * apps. Before this existed, 56 of 67 form files had no validation at
 * all and the five that did each rolled their own email regex — so
 * "valid email" meant something slightly different on every screen.
 *
 * Contract for every rule: take the raw field value, return `null` when
 * the value is acceptable or a human-readable message when it is not.
 * That uniform shape is what lets `useFormValidation` compose arbitrary
 * rule lists without knowing what any individual rule does.
 *
 * Rules never throw and never mutate. A rule handed `null`/`undefined`
 * treats it as empty — only `required()` objects to emptiness, so
 * optional fields compose correctly (`[maxLength(50), email()]` on a
 * blank optional field passes).
 */

export type Validator = (value: unknown) => string | null;

/** Normalise any field value to a string for text-shaped rules. */
function str(v: unknown): string {
  if (v == null) return '';
  return typeof v === 'string' ? v : String(v);
}

/** True when the value carries no user content. */
function isEmpty(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/**
 * Run a rule list, returning the FIRST failure.
 *
 * First-failure rather than all-failures: stacking "Required" and
 * "Must be a valid email" under one input is noise, and the second is
 * meaningless while the first holds.
 */
export function runRules(value: unknown, rules: ReadonlyArray<Validator>): string | null {
  for (const rule of rules) {
    const err = rule(value);
    if (err) return err;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Presence                                                            */
/* ------------------------------------------------------------------ */

/**
 * Required. Whitespace-only input counts as empty — a field holding
 * three spaces satisfies no business rule anywhere in this codebase.
 */
export const required = (label = 'This field'): Validator => (v) =>
  isEmpty(v) ? `${label} is required.` : null;

/** Required for a checkbox that must be ticked (terms, confirmations). */
export const mustBeChecked = (label = 'This box'): Validator => (v) =>
  v === true ? null : `${label} must be checked.`;

/**
 * Required for a dropdown / radio group.
 *
 * Treats the common "unselected" sentinels as empty, not just `''` —
 * `SearchableSelect` and the raw `<select>` elements in this codebase
 * variously use `''`, `'0'`, and the literal `'select'` placeholder.
 */
export const requiredSelection = (label = 'A selection'): Validator => (v) => {
  const s = str(v).trim().toLowerCase();
  if (s === '' || s === '0' || s === 'select' || s === 'none') {
    return `${label} is required.`;
  }
  return null;
};

/* ------------------------------------------------------------------ */
/* Length                                                              */
/* ------------------------------------------------------------------ */

export const minLength = (n: number, label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  return str(v).trim().length < n ? `${label} must be at least ${n} characters.` : null;
};

export const maxLength = (n: number, label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  return str(v).trim().length > n ? `${label} must be ${n} characters or fewer.` : null;
};

/* ------------------------------------------------------------------ */
/* Numbers                                                             */
/* ------------------------------------------------------------------ */

/** Digits only — no sign, no separator, no decimal point. */
export const numericOnly = (label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  return /^\d+$/.test(str(v).trim()) ? null : `${label} may contain digits only.`;
};

/** Whole number, optionally signed. */
export const integer = (label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  return /^-?\d+$/.test(str(v).trim()) ? null : `${label} must be a whole number.`;
};

/**
 * Decimal with a bounded scale.
 *
 * `scale` defaults to 2 because every money column in this schema is
 * `decimal(18,2)` — accepting 3dp in the UI produces a value the server
 * silently rounds, which is how a quoted total stops matching the
 * invoiced one.
 */
export const decimal = (scale = 2, label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v).trim();
  if (!/^-?\d+(\.\d+)?$/.test(s)) return `${label} must be a number.`;
  const frac = s.split('.')[1];
  if (frac && frac.length > scale) {
    return `${label} may have at most ${scale} decimal place${scale === 1 ? '' : 's'}.`;
  }
  return null;
};

/** Money: non-negative, at most 2dp. */
export const currency = (label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const dec = decimal(2, label)(v);
  if (dec) return dec;
  return Number(str(v)) < 0 ? `${label} cannot be negative.` : null;
};

export const min = (n: number, label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const num = Number(str(v));
  if (Number.isNaN(num)) return `${label} must be a number.`;
  return num < n ? `${label} must be at least ${n}.` : null;
};

export const max = (n: number, label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const num = Number(str(v));
  if (Number.isNaN(num)) return `${label} must be a number.`;
  return num > n ? `${label} must be at most ${n}.` : null;
};

/* ------------------------------------------------------------------ */
/* Formats                                                             */
/* ------------------------------------------------------------------ */

/**
 * Email.
 *
 * Deliberately NOT RFC 5322 — a full-grammar regex accepts addresses no
 * mail provider will deliver to and is unreadable at review time. This
 * checks the shape users actually get wrong: a single @, a dot-bearing
 * domain, no whitespace, no consecutive dots.
 */
export const email = (label = 'Email'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v).trim();
  if (/\s/.test(s)) return `${label} cannot contain spaces.`;
  if (!/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(s)) return `Enter a valid ${label.toLowerCase()}.`;
  if (/\.\./.test(s)) return `Enter a valid ${label.toLowerCase()}.`;
  return null;
};

/**
 * Mobile number — configurable length and allowed prefixes.
 *
 * Defaults to the Indian 10-digit / 6-9 leading-digit rule, matching
 * the existing `validateMobile` in the panel, but parameterised so a
 * tenant in another country isn't forced through a false negative.
 * A leading `+<cc>` and separators are stripped before checking.
 */
export const mobile = (
  opts: { length?: number; prefixes?: string; label?: string } = {},
): Validator => (v) => {
  const { length = 10, prefixes = '6-9', label = 'Mobile number' } = opts;
  if (isEmpty(v)) return null;
  const digits = str(v).replace(/[\s\-()+]/g, '');
  if (!/^\d+$/.test(digits)) return `${label} may contain digits only.`;
  // Tolerate a country code prefix ahead of the national number.
  const national = digits.length > length ? digits.slice(-length) : digits;
  if (national.length !== length) return `${label} must be ${length} digits.`;
  if (prefixes && !new RegExp(`^[${prefixes}]`).test(national)) {
    return `${label} must start with ${prefixes.replace('-', '–')}.`;
  }
  return null;
};

/**
 * URL. Requires an explicit http/https scheme — a bare `example.com`
 * stored and later rendered as an anchor resolves relative to the
 * current origin and produces a broken link.
 */
export const url = (label = 'URL'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v).trim();
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return `${label} must start with http:// or https://.`;
    }
    if (!u.hostname.includes('.')) return `Enter a valid ${label.toLowerCase()}.`;
    return null;
  } catch {
    return `Enter a valid ${label.toLowerCase()}.`;
  }
};

/* ------------------------------------------------------------------ */
/* Dates and times                                                     */
/* ------------------------------------------------------------------ */

/**
 * Calendar date with optional bounds.
 *
 * Rejects the classic typo years (0007, 20255) via an explicit range
 * rather than trusting `Date.parse`, which happily accepts them. Also
 * rejects roll-over dates: `2026-02-31` parses to 3 March in JS, so the
 * round-trip comparison below catches what `new Date()` silently fixes.
 */
export const date = (
  opts: { min?: string; max?: string; label?: string } = {},
): Validator => (v) => {
  const { label = 'Date' } = opts;
  if (isEmpty(v)) return null;
  const s = str(v).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return `${label} must be a valid date.`;
  const [, y, mo, d] = m;
  const year = Number(y);
  if (year < 1900 || year > 2200) return `${label} has an invalid year.`;
  const dt = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(dt.getTime())) return `${label} must be a valid date.`;
  // Round-trip guard for 31 Feb and friends.
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() + 1 !== Number(mo) ||
    dt.getUTCDate() !== Number(d)
  ) {
    return `${label} must be a valid date.`;
  }
  if (opts.min && s < opts.min) return `${label} cannot be before ${opts.min}.`;
  if (opts.max && s > opts.max) return `${label} cannot be after ${opts.max}.`;
  return null;
};

/** `HH:mm` or `HH:mm:ss`, 24-hour. */
export const time = (label = 'Time'): Validator => (v) => {
  if (isEmpty(v)) return null;
  return /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(str(v).trim())
    ? null
    : `${label} must be a valid time (HH:mm).`;
};

/**
 * Cross-field: end must be strictly after start. Returns a rule bound
 * to the other end of the range, so it composes into a normal rule list.
 */
export const afterTime = (startValue: unknown, label = 'End time'): Validator => (v) => {
  if (isEmpty(v) || isEmpty(startValue)) return null;
  return str(v) > str(startValue) ? null : `${label} must be after the start time.`;
};

/** Cross-field: end date must be on or after start date. */
export const afterDate = (startValue: unknown, label = 'End date'): Validator => (v) => {
  if (isEmpty(v) || isEmpty(startValue)) return null;
  return str(v) >= str(startValue) ? null : `${label} cannot be before the start date.`;
};

/* ------------------------------------------------------------------ */
/* Passwords                                                           */
/* ------------------------------------------------------------------ */

/**
 * Password policy: 8+ chars with an upper, a lower, a digit and a
 * symbol. Mirrors what the API's own registration path enforces, so a
 * password accepted by the form is never rejected by the server.
 */
export const password = (label = 'Password'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v);
  if (s.length < 8) return `${label} must be at least 8 characters.`;
  if (s.length > 128) return `${label} must be 128 characters or fewer.`;
  if (!/[a-z]/.test(s)) return `${label} must include a lowercase letter.`;
  if (!/[A-Z]/.test(s)) return `${label} must include an uppercase letter.`;
  if (!/\d/.test(s)) return `${label} must include a number.`;
  if (!/[^A-Za-z0-9]/.test(s)) return `${label} must include a symbol.`;
  return null;
};

/** Confirm-password. Bound to the first field's current value. */
export const matches = (other: unknown, label = 'Passwords'): Validator => (v) => {
  if (isEmpty(v)) return null;
  return str(v) === str(other) ? null : `${label} do not match.`;
};

/* ------------------------------------------------------------------ */
/* Text hygiene                                                        */
/* ------------------------------------------------------------------ */

/** Reject leading/trailing whitespace outright (vs. silently trimming). */
export const noEdgeSpaces = (label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v);
  return s !== s.trim() ? `${label} cannot start or end with a space.` : null;
};

/** Reject runs of two or more spaces. */
export const noRepeatedSpaces = (label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  return /\s{2,}/.test(str(v)) ? `${label} cannot contain repeated spaces.` : null;
};

/**
 * Allowlist of permitted characters.
 *
 * Allowlist rather than blocklist: enumerating what is permitted fails
 * closed, whereas a blocklist silently admits every character nobody
 * thought to ban.
 */
export const allowedChars = (
  pattern: RegExp,
  message = 'contains characters that are not allowed',
  label = 'This field',
): Validator => (v) => {
  if (isEmpty(v)) return null;
  return pattern.test(str(v)) ? null : `${label} ${message}.`;
};

/** Letters, spaces, hyphen and apostrophe — person and place names. */
export const nameChars = (label = 'This field'): Validator =>
  allowedChars(/^[\p{L}\s'’-]+$/u, 'may contain letters, spaces, hyphens and apostrophes only', label);

/** Letters, digits, space and basic punctuation — codes, SKUs, titles. */
export const alphanumericChars = (label = 'This field'): Validator =>
  allowedChars(/^[\p{L}\p{N}\s._/-]+$/u, 'may contain letters, numbers and . _ / - only', label);

/* ------------------------------------------------------------------ */
/* Injection defence                                                   */
/* ------------------------------------------------------------------ */

/**
 * SQL-injection shapes.
 *
 * This is defence in depth and NOTHING MORE. The API uses EF Core with
 * parameterised queries, which is the actual protection; a regex over
 * user text cannot be one, because every pattern here has a legitimate
 * counter-example ("Drop-in replacement", "Smith--Jones", a French
 * surname carrying an apostrophe).
 *
 * The patterns are therefore deliberately narrow — SQL *syntax* in
 * positions no ordinary field value uses — rather than bare keywords.
 * A blanket ban on the word "update" would reject product descriptions
 * for no security gain.
 */
const SQL_PATTERNS: ReadonlyArray<RegExp> = [
  /(^|\s)(union\s+all\s+select|union\s+select)\b/i,
  /\b(drop|truncate)\s+(table|database)\b/i,
  /\b(delete\s+from|insert\s+into)\b/i,
  /\bupdate\b[\s\S]{0,40}\bset\b[\s\S]{0,40}=/i,
  /\bexec(ute)?\s*\(/i,
  /\bxp_cmdshell\b/i,
  /(--|#)\s*$/,
  /;\s*(drop|delete|update|insert|select)\b/i,
  /'\s*(or|and)\s+'?\d+'?\s*=\s*'?\d+/i,
  /\bor\s+1\s*=\s*1\b/i,
];

export const noSqlInjection = (label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v);
  return SQL_PATTERNS.some((re) => re.test(s))
    ? `${label} contains text that looks like a database command.`
    : null;
};

/**
 * XSS shapes — markup and script-bearing URLs.
 *
 * Same caveat: React escapes interpolated text by default, so this is a
 * second line, not the first. It matters most for values that later
 * reach `dangerouslySetInnerHTML` (the CMS page body) or are echoed
 * into a printed document.
 */
const XSS_PATTERNS: ReadonlyArray<RegExp> = [
  /<\s*script\b/i,
  /<\s*\/?\s*(iframe|object|embed|svg|img|link|style|meta|base|form)\b/i,
  /\bon[a-z]+\s*=\s*["'`]?/i,
  /javascript\s*:/i,
  /data\s*:\s*text\/html/i,
  /<\s*[a-z][^>]*>/i,
];

export const noXss = (label = 'This field'): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v);
  return XSS_PATTERNS.some((re) => re.test(s))
    ? `${label} cannot contain HTML or script content.`
    : null;
};

/** Both injection guards — the default for any free-text field. */
export const safeText = (label = 'This field'): ReadonlyArray<Validator> => [
  noSqlInjection(label),
  noXss(label),
];

/* ------------------------------------------------------------------ */
/* Collections and files                                               */
/* ------------------------------------------------------------------ */

/**
 * Duplicate guard. `existing` is the set of values already used;
 * comparison is case-insensitive and trimmed, because "Main Store" and
 * "main store " are the same store to everyone except a byte compare.
 */
export const notDuplicate = (
  existing: ReadonlyArray<string>,
  label = 'This value',
): Validator => (v) => {
  if (isEmpty(v)) return null;
  const s = str(v).trim().toLowerCase();
  return existing.some((e) => String(e).trim().toLowerCase() === s)
    ? `${label} is already in use.`
    : null;
};

export interface FileRules {
  /** Permitted extensions, without the dot. */
  accept?: ReadonlyArray<string>;
  /** Ceiling in megabytes. */
  maxSizeMb?: number;
  label?: string;
}

/**
 * File type and size. Checks the EXTENSION as well as the MIME type:
 * a browser reports `application/octet-stream` for plenty of ordinary
 * files, so trusting `file.type` alone rejects valid uploads, while
 * trusting the extension alone accepts a renamed one. Neither is a
 * security control — the server must re-check.
 */
export const file = (rules: FileRules = {}): Validator => (v) => {
  const { accept, maxSizeMb, label = 'File' } = rules;
  if (v == null) return null;
  const f = v as File;
  if (typeof f !== 'object' || typeof f.name !== 'string') return null;
  if (accept && accept.length > 0) {
    const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
    if (!accept.map((a) => a.toLowerCase()).includes(ext)) {
      return `${label} must be one of: ${accept.join(', ')}.`;
    }
  }
  if (maxSizeMb != null && f.size > maxSizeMb * 1024 * 1024) {
    return `${label} must be ${maxSizeMb} MB or smaller.`;
  }
  return null;
};

/* ------------------------------------------------------------------ */
/* Input sanitisers (applied while typing / on paste)                  */
/* ------------------------------------------------------------------ */

/**
 * Sanitisers differ from validators: they RESHAPE the value as the user
 * types rather than reporting on it. `useFormValidation` runs the
 * field's sanitiser on both keystrokes and paste, which is what makes a
 * pasted value obey the same limits as a typed one — the brief calls
 * this out specifically, and it is the case hand-rolled `onChange`
 * handlers in this codebase consistently miss.
 */
export type Sanitizer = (raw: string) => string;

/** Strip everything but digits, then cap the length. */
export const digitsOnly = (maxLen?: number): Sanitizer => (raw) => {
  const d = raw.replace(/\D/g, '');
  return maxLen != null ? d.slice(0, maxLen) : d;
};

/** Digits plus one decimal point, capped to `scale` decimal places. */
export const decimalInput = (scale = 2): Sanitizer => (raw) => {
  let s = raw.replace(/[^\d.]/g, '');
  const first = s.indexOf('.');
  if (first !== -1) {
    // Keep the first dot, drop the rest.
    s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, '');
    const [whole, frac = ''] = s.split('.');
    s = frac.length > scale ? `${whole}.${frac.slice(0, scale)}` : s;
  }
  return s;
};

/** Hard character cap — the "enforce max length while typing" rule. */
export const capLength = (n: number): Sanitizer => (raw) => raw.slice(0, n);

/** Collapse whitespace runs to a single space. Does not trim edges — */
/** trimming mid-typing would stop the user ever entering a space.     */
export const collapseSpaces: Sanitizer = (raw) => raw.replace(/\s{2,}/g, ' ');

/** Drop characters outside the allowlist as they are typed. */
export const stripDisallowed = (pattern: RegExp): Sanitizer => (raw) =>
  raw.split('').filter((ch) => pattern.test(ch)).join('');

/** Compose sanitisers left to right. */
export const chain = (...fns: ReadonlyArray<Sanitizer>): Sanitizer => (raw) =>
  fns.reduce((acc, fn) => fn(acc), raw);
